import React, { useState, useEffect } from 'react';

const ChatGPTPanel = ({ vehicleData, onResponse }) => {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [error, setError] = useState('');
  const [hasData, setHasData] = useState(false);

  // Check if we have enough vehicle data to make a request
  useEffect(() => {
    const requiredFields = ['year', 'make', 'model', 'currentMiles'];
    const hasRequiredData = requiredFields.every(field => vehicleData[field] && vehicleData[field] !== 'Unknown');
    setHasData(hasRequiredData);
  }, [vehicleData]);



  const buildPrompt = () => {
    const { year, make, model, trim, bodyClass, currentMiles, driveType, vehicleColor } = vehicleData;
    
    // Build vehicle description with only known fields
    let vehicleDescription = `${year} ${make} ${model}`;
    if (trim && trim !== 'Unknown') vehicleDescription += ` ${trim}`;
    
    // Build equipment list with only known fields
    let equipmentList = `${vehicleDescription}\n`;
    if (bodyClass && bodyClass !== 'Unknown') equipmentList += `Body Style: ${bodyClass}\n`;
    if (vehicleColor && vehicleColor.trim() !== '') equipmentList += `Exterior Color: ${vehicleColor}\n`;
    if (currentMiles) equipmentList += `Odometer: ${currentMiles} miles\n`;
    if (driveType && driveType !== 'Unknown') equipmentList += `Drivetrain: ${driveType}\n`;
    
    return `You are a professional vehicle appraiser with extensive knowledge of vehicle values. Based on typical market conditions and depreciation patterns, estimate the trade-in value for this vehicle:

${equipmentList}
Consider these factors in your estimate:
- Vehicle age and typical depreciation
- Mileage impact on value  
- Commercial vehicle market trends
- Dealer trade-in margins (typically 60-70% of retail value)

Provide a realistic trade-in estimate based on your knowledge of similar vehicles in the current market.

Return ONLY the calculated amount in this format: **Estimated Trade-In Value: $XX,XXX**`;
  };

  const getValuation = async () => {
    if (!hasData) return;
    
    setLoading(true);
    setError('');
    setResponse('');
    
    try {
      const prompt = buildPrompt();
      
      // Log the exact prompt being sent to ChatGPT
      console.log('=== ChatGPT Prompt ===');
      console.log(prompt);
      console.log('=====================');
      
      // Since we can't directly call OpenAI API from frontend due to CORS and API key security,
      // we'll need to create a backend endpoint. For now, I'll create a mock response
      // In production, this would call your backend API endpoint
      
      const response = await fetch('/api/chatgpt-valuation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to get valuation');
      }
      
      const data = await response.json();
      
      // Extract dollar amount from response and reduce by 10%
      let processedResponse = data.response;
      const dollarMatch = processedResponse.match(/\$[\d,]+/);
      
      if (dollarMatch) {
        const originalAmount = parseInt(dollarMatch[0].replace(/[$,]/g, ''));
        const reducedAmount = Math.round(originalAmount * 0.9);
        const formattedAmount = `$${reducedAmount.toLocaleString()}`;
        processedResponse = processedResponse.replace(dollarMatch[0], formattedAmount);
      }
      
      setResponse(processedResponse);
      
      if (onResponse) {
        onResponse(processedResponse);
      }
    } catch (err) {
      setError('Unable to get valuation at this time. Please try again later.');
      console.error('ChatGPT API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatResponse = (text) => {
    // Simple formatting to handle markdown-like bold text
    let formatted = text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-green-300 text-lg">$1</strong>')
      .replace(/\n/g, '<br>');
    
    return formatted;
  };

  return (
    <div className="bg-white/15 backdrop-blur-lg rounded-2xl border border-white/30 p-6 shadow-2xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <svg className="w-6 h-6 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          AI Trade-In Valuation
        </h3>
        <button
          onClick={getValuation}
          disabled={loading || !hasData}
          className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {loading ? (
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Getting Valuation...
            </div>
          ) : (
            'Get Valuation'
          )}
        </button>
      </div>
      
      {!hasData && (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-gray-400 text-sm">
            Complete the vehicle information to get an AI-powered trade-in valuation
          </p>
        </div>
      )}
      
      {error && (
        <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        </div>
      )}
      
      {response && (
        <div className="mt-4">
          <div className="bg-gradient-to-br from-white/15 to-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/30 shadow-lg">
            <div 
              className="text-center text-white text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatResponse(response) }}
            />
          </div>
        </div>
      )}
      
      {hasData && !loading && !response && !error && (
        <div className="text-center py-6">
          <div className="text-gray-400 mb-3">
            <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-400 text-sm">
            Click "Get Valuation" to receive an AI-powered trade-in analysis
          </p>
        </div>
      )}
    </div>
  );
};

export default ChatGPTPanel; 